// cSpell:ignore AUTOPOL, Champus, LIAB, MCPOL, medib, PUBLICPOL, WCBPOL
import { NetworkType } from 'candidhealth/api/index.js';
import type { InsurancePlanType as BaseInsurancePlanType, ValueSetsConfig } from 'config-types/config/value-sets';
import { deepFreezeObject } from '../../utils/objects';

// Extend InsurancePlanType to use the specific Candid NetworkType
export interface InsurancePlanType extends Omit<BaseInsurancePlanType, 'candidCode'> {
  candidCode: NetworkType;
}

export const insuranceTypeOptionsData = [
  {
    candidCode: '09',
    label: 'Self Pay',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/coverage-selfpay',
      code: 'pay',
    },
  },
  {
    candidCode: '11',
    label: 'Other Non-Federal Programs',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'PUBLICPOL',
    },
  },
  {
    candidCode: '12',
    label: 'PPO',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'PPO',
    },
  },
  {
    candidCode: '13',
    label: 'POS',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'POS',
    },
  },
  {
    candidCode: '14',
    label: 'EPO',
    coverageCoding: {
      system: 'https://nahdo.org/sopt',
      code: '515',
      display: 'Commercial Managed Care - EPO',
    },
  },
  {
    candidCode: '15',
    label: 'Indemnity Insurance',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'HIP',
      display: 'health insurance plan',
    },
  },
  {
    candidCode: '16',
    label: 'HMO Medicare Risk',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'MCPOL',
      display: 'managed care policy',
    },
  },
  {
    candidCode: '17',
    label: 'DMO',
    coverageCoding: {
      system: 'http://nucc.org/provider-taxonomy',
      code: '1223D0001X',
      display: 'Dental, Dental Public Health',
    },
  },
  {
    candidCode: 'AM',
    label: 'Auto',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'AUTOPOL',
    },
  },
  {
    candidCode: 'BL',
    label: 'BlueCross BlueShield',
  },
  {
    candidCode: 'CH',
    label: 'Champus',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'PUBLICPOL',
      display: 'public healthcare',
    },
  },
  {
    candidCode: 'CI',
    label: 'Commercial Insurance Co',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'COMM',
      display: 'Commercial',
    },
  },
  {
    candidCode: 'DS',
    label: 'Disability',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'DIS',
    },
  },
  {
    candidCode: 'FI',
    label: 'Federal Employees',
    coverageCoding: {
      system: 'http://hl7.org/fhir/us/directory-attestation/CodeSystem/InsuranceProductTypeCS',
      code: 'fep',
      display: 'Federal Employee Program',
    },
  },
  {
    candidCode: 'HM',
    label: 'HMO',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'HMO',
    },
  },
  {
    candidCode: 'LM',
    label: 'Liability',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'LIAB',
      display: 'liability insurance',
    },
  },
  {
    candidCode: 'MA',
    label: 'Medicare Part A',
    coverageCoding: {
      system: 'http://hl7.org/fhir/us/directory-attestation/CodeSystem/InsuranceProductTypeCS',
      code: 'media',
      display: 'Medicare Part A',
    },
  },
  {
    candidCode: 'MB',
    label: 'Medicare Part B',
    coverageCoding: {
      system: 'http://hl7.org/fhir/us/directory-attestation/CodeSystem/InsuranceProductTypeCS',
      code: 'medib',
      display: 'Medicare Part B',
    },
  },
  {
    candidCode: 'MC',
    label: 'Medicaid',
  },
  {
    candidCode: 'OF',
    label: 'Other Federal Program',
  },
  {
    candidCode: 'TV',
    label: 'Title V',
  },
  {
    candidCode: 'VA',
    label: 'Veterans Affairs Plan',
    coverageCoding: {
      system: 'http://hl7.org/fhir/us/directory-attestation/CodeSystem/InsuranceProductTypeCS',
      code: 'va',
      display: 'Veterans Affairs',
    },
  },
  {
    candidCode: 'WC',
    label: 'Workers Comp Health Claim',
    coverageCoding: {
      system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode',
      code: 'WCBPOL',
    },
  },
  {
    candidCode: 'ZZ',
    label: 'Mutually Defined',
  },
];

export const formValueSetsData = {
  birthSexOptions: [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
    { label: 'Intersex', value: 'Intersex' },
  ],
  cancelReasonOptionsInPersonPatient: [
    { label: 'Patient improved', value: 'Patient improved' },
    { label: 'Wait time too long', value: 'Wait time too long' },
    { label: 'Prefer another provider', value: 'Prefer another provider' },
    { label: 'Changing location', value: 'Changing location' },
    { label: 'Changing to telemedicine', value: 'Changing to telemedicine' },
    { label: 'Financial responsibility concern', value: 'Financial responsibility concern' },
    { label: 'Insurance issue', value: 'Insurance issue' },
    { label: 'Other', value: 'Other' },
  ],
  cancelReasonOptionsInPersonProvider: [
    { label: 'Patient improved', value: 'Patient improved' },
    { label: 'Wait time too long', value: 'Wait time too long' },
    { label: 'Prefer another provider', value: 'Prefer another provider' },
    { label: 'Changing location', value: 'Changing location' },
    { label: 'Changing to telemedicine', value: 'Changing to telemedicine' },
    { label: 'Financial responsibility concern', value: 'Financial responsibility concern' },
    { label: 'Insurance issue', value: 'Insurance issue' },
    { label: 'Service never offered', value: 'Service never offered' },
    { label: 'Duplicate visit or account error', value: 'Duplicate visit or account error' },
    { label: 'Provider deems acuity too high for clinic', value: 'Provider deems acuity too high for clinic' },
    { label: 'Other', value: 'Other' },
  ],
  cancelReasonOptionsVirtualPatient: [
    { label: 'Wait time too long', value: 'Wait time too long' },
    { label: 'Using another telemedicine service', value: 'Using another telemedicine service' },
    { label: 'Prefer in-person visit', value: 'Prefer in-person visit' },
    { label: 'Financial concern/Insurance issue', value: 'Financial concern/Insurance issue' },
    { label: 'Technical issue', value: 'Technical issue' },
    { label: 'Patient improved', value: 'Patient improved' },
    { label: 'Clicked accidentally', value: 'Clicked accidentally' },
    { label: 'Other', value: 'Other' },
  ],
  cancelReasonOptionsVirtualProvider: [
    {
      label: 'Patient did not answer after multiple attempts',
      value: 'Patient did not answer after multiple attempts',
    },
    { label: 'Wrong patient name on chart', value: 'Wrong patient name on chart' },
    {
      label: 'Technical issues connecting and/ or with video',
      value: 'Technical issues connecting and/ or with video',
    },
    { label: 'Other', value: 'Other' },
  ],
  emergencyContactRelationshipOptions: [
    { label: 'Spouse', value: 'Spouse' },
    { label: 'Parent', value: 'Parent' },
    { label: 'Legal Guardian', value: 'Legal Guardian' },
    { label: 'Other', value: 'Other' },
  ],
  ethnicityOptions: [
    { label: 'Hispanic or Latino', value: 'Hispanic or Latino' },
    { label: 'Not Hispanic or Latino', value: 'Not Hispanic or Latino' },
    { label: 'Decline to Specify', value: 'Decline to Specify' },
  ],
  genderIdentityOptions: [
    { label: 'Female', value: 'Female gender identity' },
    { label: 'Male', value: 'Male gender identity' },
    { label: 'Other', value: 'Non-binary gender identity' },
  ],
  insurancePriorityOptions: [
    { label: 'Primary', value: 'Primary' },
    { label: 'Secondary', value: 'Secondary' },
  ],
  languageOptions: [
    { label: 'English', value: 'English' },
    { label: 'Spanish', value: 'Spanish' },
    { label: 'Chinese', value: 'Chinese' },
    { label: 'French', value: 'French' },
    { label: 'German', value: 'German' },
    { label: 'Tagalog', value: 'Tagalog' },
    { label: 'Vietnamese', value: 'Vietnamese' },
    { label: 'Italian', value: 'Italian' },
    { label: 'Korean', value: 'Korean' },
    { label: 'Russian', value: 'Russian' },
    { label: 'Polish', value: 'Polish' },
    { label: 'Arabic', value: 'Arabic' },
    { label: 'Portuguese', value: 'Portuguese' },
    { label: 'Japanese', value: 'Japanese' },
    { label: 'Greek', value: 'Greek' },
    { label: 'Hindi', value: 'Hindi' },
    { label: 'Persian', value: 'Persian' },
    { label: 'Urdu', value: 'Urdu' },
    { label: 'Sign Language', value: 'Sign Language' },
    { label: 'Other', value: 'Other' },
  ],
  patientFillingOutAsOptions: [
    { label: 'Parent', value: 'Parent' },
    { label: 'Patient', value: 'Patient' },
  ],
  pointOfDiscoveryOptions: [
    { label: 'Friend/Family', value: 'Friend/Family' },
    { label: 'Been there with another family member', value: 'Been there with another family member' },
    { label: 'Healthcare Professional', value: 'Healthcare Professional' },
    { label: 'Google/Internet search', value: 'Google/Internet search' },
    { label: 'Internet ad', value: 'Internet ad' },
    { label: 'Social media community group', value: 'Social media community group' },
    { label: 'Webinar', value: 'Webinar' },
    { label: 'TV/Radio', value: 'TV/Radio' },
    { label: 'Newsletter', value: 'Newsletter' },
    { label: 'School', value: 'School' },
    { label: 'Drive by/Signage', value: 'Drive by/Signage' },
  ],
  preferredCommunicationMethodOptions: [
    { label: 'No preference', value: 'No preference' },
    { label: 'Email', value: 'Email' },
    { label: 'Home Phone', value: 'Home Phone' },
    { label: 'Cell Phone', value: 'Cell Phone' },
    { label: 'Mail', value: 'Mail' },
  ],
  pronounOptions: [
    { label: 'He/him', value: 'He/him' },
    { label: 'She/her', value: 'She/her' },
    { label: 'They/them', value: 'They/them' },
    { label: 'My pronouns are not listed', value: 'My pronouns are not listed' },
  ],
  raceOptions: [
    { label: 'American Indian or Alaska Native', value: 'American Indian or Alaska Native' },
    { label: 'Asian', value: 'Asian' },
    { label: 'Black or African American', value: 'Black or African American' },
    { label: 'Native Hawaiian or Other Pacific Islander', value: 'Native Hawaiian or Other Pacific Islander' },
    { label: 'White', value: 'White' },
    { label: 'Decline to Specify', value: 'Decline to Specify' },
  ],
  relationshipOptions: [
    { label: 'Self', value: 'Self' },
    { label: 'Spouse', value: 'Spouse' },
    { label: 'Parent', value: 'Parent' },
    { label: 'Legal Guardian', value: 'Legal Guardian' },
    { label: 'Other', value: 'Other' },
  ],
  relationshipToInsuredOptions: [
    { label: 'Self', value: 'Self' },
    { label: 'Child', value: 'Child' },
    { label: 'Parent', value: 'Parent' },
    { label: 'Spouse', value: 'Spouse' },
    { label: 'Common Law Spouse', value: 'Common Law Spouse' },
    { label: 'Injured Party', value: 'Injured Party' },
    { label: 'Other', value: 'Other' },
  ],
  rxHistoryConsentOptions: [
    { label: 'Rx history consent signed by the patient', value: 'Rx history consent signed by the patient' },
    { label: 'Rx history consent unasked to the patient', value: 'Rx history consent unasked to the patient' },
    { label: 'Rx history consent denied by the patient', value: 'Rx history consent denied by the patient' },
  ],
  sexualOrientationOptions: [
    { label: 'Straight', value: 'Straight' },
    { label: 'Lesbian or Gay', value: 'Lesbian or Gay' },
    { label: 'Bisexual', value: 'Bisexual' },
    { label: 'Something else', value: 'Something else' },
    { label: 'Decline to Specify', value: 'Decline to Specify' },
  ],
  stateOptions: [
    { label: 'AL', value: 'AL' },
    { label: 'AK', value: 'AK' },
    { label: 'AZ', value: 'AZ' },
    { label: 'AR', value: 'AR' },
    { label: 'CA', value: 'CA' },
    { label: 'CO', value: 'CO' },
    { label: 'CT', value: 'CT' },
    { label: 'DE', value: 'DE' },
    { label: 'DC', value: 'DC' },
    { label: 'FL', value: 'FL' },
    { label: 'GA', value: 'GA' },
    { label: 'HI', value: 'HI' },
    { label: 'ID', value: 'ID' },
    { label: 'IL', value: 'IL' },
    { label: 'IN', value: 'IN' },
    { label: 'IA', value: 'IA' },
    { label: 'KS', value: 'KS' },
    { label: 'KY', value: 'KY' },
    { label: 'LA', value: 'LA' },
    { label: 'ME', value: 'ME' },
    { label: 'MD', value: 'MD' },
    { label: 'MA', value: 'MA' },
    { label: 'MI', value: 'MI' },
    { label: 'MN', value: 'MN' },
    { label: 'MS', value: 'MS' },
    { label: 'MO', value: 'MO' },
    { label: 'MT', value: 'MT' },
    { label: 'NE', value: 'NE' },
    { label: 'NV', value: 'NV' },
    { label: 'NH', value: 'NH' },
    { label: 'NJ', value: 'NJ' },
    { label: 'NM', value: 'NM' },
    { label: 'NY', value: 'NY' },
    { label: 'NC', value: 'NC' },
    { label: 'ND', value: 'ND' },
    { label: 'OH', value: 'OH' },
    { label: 'OK', value: 'OK' },
    { label: 'OR', value: 'OR' },
    { label: 'PA', value: 'PA' },
    { label: 'RI', value: 'RI' },
    { label: 'SC', value: 'SC' },
    { label: 'SD', value: 'SD' },
    { label: 'TN', value: 'TN' },
    { label: 'TX', value: 'TX' },
    { label: 'UT', value: 'UT' },
    { label: 'VT', value: 'VT' },
    { label: 'VA', value: 'VA' },
    { label: 'VI', value: 'VI' },
    { label: 'WA', value: 'WA' },
    { label: 'WV', value: 'WV' },
    { label: 'WI', value: 'WI' },
    { label: 'WY', value: 'WY' },
  ],
  patientPaymentPageOptions: [
    { label: 'I have insurance', value: 'I have insurance' },
    { label: 'I will pay without insurance', value: 'I will pay without insurance' },
  ],
  patientOccMedPaymentPageOptions: [
    { label: 'Self', value: 'Self' },
    { label: 'Employer', value: 'Employer' },
  ],
  currentMedicationsOptions: [
    { label: 'Acetaminophen/ Tylenol', value: 'Acetaminophen/ Tylenol' },
    { label: 'Albuterol', value: 'Albuterol' },
    { label: 'Amoxicillin', value: 'Amoxicillin' },
    { label: 'Amoxicillin+Clav./ Augmentin', value: 'Amoxicillin+Clav./ Augmentin' },
    { label: 'Budesonide inhaler/ Pulmicort/ Symbicort', value: 'Budesonide inhaler/ Pulmicort/ Symbicort' },
    { label: 'Cefdinir/ Omnicef', value: 'Cefdinir/ Omnicef' },
    { label: 'Cephalexin/ Keflex', value: 'Cephalexin/ Keflex' },
    { label: 'Cetirizine/ Zyrtec', value: 'Cetirizine/ Zyrtec' },
    { label: 'cloNIDine', value: 'cloNIDine' },
    { label: 'Cold, cough medication- over the counter', value: 'Cold, cough medication- over the counter' },
    {
      label: 'Dextroamphetamine amphetamine/ Adderall/ Focalin',
      value: 'Dextroamphetamine amphetamine/ Adderall/ Focalin',
    },
    { label: 'Diphenhydramine/ Benadryl', value: 'Diphenhydramine/ Benadryl' },
    { label: 'EpiPen', value: 'EpiPen' },
    { label: 'Escitalopram/ Lexapro', value: 'Escitalopram/ Lexapro' },
    { label: 'Famotidine/ Pepcid/ Zantac', value: 'Famotidine/ Pepcid/ Zantac' },
    { label: 'Fexofenadine/ Allegra', value: 'Fexofenadine/ Allegra' },
    { label: 'Fluoxetine/ PROzac', value: 'Fluoxetine/ PROzac' },
    { label: 'Fluticasone propionate inhaler/ Flovent', value: 'Fluticasone propionate inhaler/ Flovent' },
    { label: 'Fluticasone propionate nasal spray/ Flonase', value: 'Fluticasone propionate nasal spray/ Flonase' },
    { label: 'guanFACINE HCl/ Intuniv', value: 'guanFACINE HCl/ Intuniv' },
    { label: 'Ibuprofen/ Motrin/ Advil', value: 'Ibuprofen/ Motrin/ Advil' },
    { label: 'Levocetirizine/ Xyzal', value: 'Levocetirizine/ Xyzal' },
    { label: 'Lisdexamfetamine/ Vyvanse', value: 'Lisdexamfetamine/ Vyvanse' },
    { label: 'Loratadine/ Claritin', value: 'Loratadine/ Claritin' },
    { label: 'Melatonin', value: 'Melatonin' },
    { label: 'Methylphenidate/ Concerta', value: 'Methylphenidate/ Concerta' },
    { label: 'MiraLax', value: 'MiraLax' },
    { label: 'Montelukast/ Singulair', value: 'Montelukast/ Singulair' },
    { label: 'Sertraline/ Zoloft', value: 'Sertraline/ Zoloft' },
    { label: 'Vitamins', value: 'Vitamins' },
  ],
  currentMedicationsYesNoOptions: [
    {
      label: 'Patient does not take any medications currently',
      value: 'Patient does not take any medications currently',
    },
    { label: 'Patient takes medication currently', value: 'Patient takes medication currently' },
  ],
  allergyTypeOptions: [
    { label: 'Medications', value: 'Medications' },
    { label: 'Other', value: 'Other' },
  ],
  allergyMedicationOptions: [
    { label: 'Acetaminophen (Tylenol)', value: 'Acetaminophen (Tylenol)' },
    { label: 'Amoxicillin/ Penicillin', value: 'Amoxicillin/ Penicillin' },
    { label: 'Amoxicillin-Clavulanic Acid (Augmentin)', value: 'Amoxicillin-Clavulanic Acid (Augmentin)' },
    { label: 'Aspirin', value: 'Aspirin' },
    { label: 'Azithromycin', value: 'Azithromycin' },
    { label: 'Cefdinir (Omnicef)', value: 'Cefdinir (Omnicef)' },
    { label: 'Cefprozil (Cefzil)', value: 'Cefprozil (Cefzil)' },
    { label: 'Cephalexin (Keflex)', value: 'Cephalexin (Keflex)' },
    { label: 'Clindamycin', value: 'Clindamycin' },
    { label: 'Erythromycin ', value: 'Erythromycin ' },
    { label: 'Ibuprofen (Motrin/ Advil)', value: 'Ibuprofen (Motrin/ Advil)' },
    { label: 'Oseltamivir (Tamiflu)', value: 'Oseltamivir (Tamiflu)' },
    { label: 'Other Cephalosporin', value: 'Other Cephalosporin' },
    {
      label: 'Trimethoprim / Sulfamethoxazole (Bactrim)/ Sulfa Antibiotics',
      value: 'Trimethoprim / Sulfamethoxazole (Bactrim)/ Sulfa Antibiotics',
    },
  ],
  allergyOtherOptions: [
    { label: 'Adhesive', value: 'Adhesive' },
    { label: 'Apple', value: 'Apple' },
    { label: 'Banana', value: 'Banana' },
    { label: 'Bees/ bee sting', value: 'Bees/ bee sting' },
    { label: 'Cockroach', value: 'Cockroach' },
    { label: 'Dust Mites', value: 'Dust Mites' },
    { label: 'Eggs or Egg-derived Products', value: 'Eggs or Egg-derived Products' },
    { label: 'Fish/ Fish Oil', value: 'Fish/ Fish Oil' },
    { label: 'Food Color Red', value: 'Food Color Red' },
    { label: 'Kiwi', value: 'Kiwi' },
    { label: 'Lactose', value: 'Lactose' },
    { label: 'Latex', value: 'Latex' },
    { label: 'Mango', value: 'Mango' },
    { label: 'Milk/ Dairy', value: 'Milk/ Dairy' },
    { label: 'Mold', value: 'Mold' },
    { label: 'Mosquito (Diagnostic)', value: 'Mosquito (Diagnostic)' },
    { label: 'Peanut', value: 'Peanut' },
    { label: 'Pet Dander (Cat, Dog, etc.) ', value: 'Pet Dander (Cat, Dog, etc.) ' },
    { label: 'Pineapple', value: 'Pineapple' },
    { label: 'Pollen/ Seasonal/ Ragweed', value: 'Pollen/ Seasonal/ Ragweed' },
    { label: 'Sesame/ Sesame Seed/ Sesame Oil', value: 'Sesame/ Sesame Seed/ Sesame Oil' },
    { label: 'Shellfish/ Seafood/ Shrimp', value: 'Shellfish/ Seafood/ Shrimp' },
    { label: 'Soy/ Soybean', value: 'Soy/ Soybean' },
    { label: 'Strawberries', value: 'Strawberries' },
    { label: 'Tree Nuts (Cashew, Pistachio, etc.)', value: 'Tree Nuts (Cashew, Pistachio, etc.)' },
    { label: 'Wheat/ Gluten', value: 'Wheat/ Gluten' },
  ],
  allergiesYesNoOptions: [
    { label: 'Patient has no known current allergies', value: 'Patient has no known current allergies' },
    { label: 'Patient has known current allergies', value: 'Patient has known current allergies' },
  ],
  medicalConditionOptions: [
    { label: 'ADHD/ ADD', value: 'ADHD/ ADD' },
    { label: 'Anemia', value: 'Anemia' },
    { label: 'Anxiety', value: 'Anxiety' },
    { label: 'Asthma/ Reactive Airway/ Wheezing', value: 'Asthma/ Reactive Airway/ Wheezing' },
    { label: 'Autism spectrum', value: 'Autism spectrum' },
    { label: 'Celiac disease', value: 'Celiac disease' },
    { label: 'Constipation', value: 'Constipation' },
    { label: 'Croup', value: 'Croup' },
    { label: 'Depression', value: 'Depression' },
    { label: 'Eczema', value: 'Eczema' },
    { label: 'Epilepsy', value: 'Epilepsy' },
    { label: 'Febrile Seizure', value: 'Febrile Seizure' },
    { label: 'G6PD deficiency', value: 'G6PD deficiency' },
    { label: 'Gastroesophageal Reflux Disease- GERD', value: 'Gastroesophageal Reflux Disease- GERD' },
    { label: 'Heart murmur', value: 'Heart murmur' },
    { label: 'Hip dysplasia- congenital', value: 'Hip dysplasia- congenital' },
    { label: 'Hydronephrosis', value: 'Hydronephrosis' },
    { label: 'Hypothyroidism', value: 'Hypothyroidism' },
    { label: 'Jaundice', value: 'Jaundice' },
    { label: 'Laryngomalacia', value: 'Laryngomalacia' },
    { label: 'Migraines', value: 'Migraines' },
    { label: 'Otitis media/ Chronic ear infections', value: 'Otitis media/ Chronic ear infections' },
    { label: 'Pneumonia', value: 'Pneumonia' },
    { label: 'Premature birth- preemie', value: 'Premature birth- preemie' },
    { label: 'RSV- Respiratory Syncytial Virus', value: 'RSV- Respiratory Syncytial Virus' },
    { label: 'Seasonal allergies', value: 'Seasonal allergies' },
    { label: 'Sickle cell trait', value: 'Sickle cell trait' },
    { label: 'Sleep apnea', value: 'Sleep apnea' },
    { label: 'Speech delay', value: 'Speech delay' },
    { label: 'Type 1 Diabetes', value: 'Type 1 Diabetes' },
  ],
  medicalHistoryYesNoOptions: [
    { label: 'Patient has no current medical conditions', value: 'Patient has no current medical conditions' },
    { label: 'Patient has current medical conditions', value: 'Patient has current medical conditions' },
  ],
  surgeryTypeOptions: [
    { label: 'Adenoidectomy (Adenoid Removal)', value: 'Adenoidectomy (Adenoid Removal)' },
    { label: 'Appendectomy', value: 'Appendectomy' },
    { label: 'C-section (Cesarean delivery)', value: 'C-section (Cesarean delivery)' },
    { label: 'Circumcision', value: 'Circumcision' },
    { label: 'Cleft lip/palate repair', value: 'Cleft lip/palate repair' },
    { label: 'Cyst removed', value: 'Cyst removed' },
    { label: 'Dental/ Oral Surgery', value: 'Dental/ Oral Surgery' },
    { label: 'Ear tube placement (Myringotomy)', value: 'Ear tube placement (Myringotomy)' },
    { label: 'Elbow/ Hand/ Arm Surgery', value: 'Elbow/ Hand/ Arm Surgery' },
    { label: 'Feeding tube (G-tube)', value: 'Feeding tube (G-tube)' },
    { label: 'Foot/ Ankle Surgery', value: 'Foot/ Ankle Surgery' },
    { label: 'Frenotomy (Tongue Tie Repair)', value: 'Frenotomy (Tongue Tie Repair)' },
    { label: 'Gallbladder removal', value: 'Gallbladder removal' },
    { label: 'Heart/ Cardiac surgery', value: 'Heart/ Cardiac surgery' },
    { label: 'Hemangioma ', value: 'Hemangioma ' },
    { label: 'Hernia ', value: 'Hernia ' },
    { label: 'Hydrocele Repair', value: 'Hydrocele Repair' },
    { label: 'Hypospadias repair', value: 'Hypospadias repair' },
    { label: 'Kidney surgery', value: 'Kidney surgery' },
    { label: 'Knee Surgery', value: 'Knee Surgery' },
    { label: 'Orchiectomy (Testicle Removal)', value: 'Orchiectomy (Testicle Removal)' },
    { label: 'Other Eye surgery', value: 'Other Eye surgery' },
    { label: 'Pyloromyotomy (Pyloric Stenosis Repair)', value: 'Pyloromyotomy (Pyloric Stenosis Repair)' },
    { label: 'Sinus surgery', value: 'Sinus surgery' },
    { label: 'Splenectomy', value: 'Splenectomy' },
    { label: 'Tear Duct Eye surgery', value: 'Tear Duct Eye surgery' },
    {
      label: 'Tonsillectomy and adenoidectomy (Tonsil and Adenoid Removal)',
      value: 'Tonsillectomy and adenoidectomy (Tonsil and Adenoid Removal)',
    },
    { label: 'Undescended Testicle Repair', value: 'Undescended Testicle Repair' },
    { label: 'Ventriculoperitoneal shunt placement', value: 'Ventriculoperitoneal shunt placement' },
    { label: 'Wisdom teeth removal', value: 'Wisdom teeth removal' },
    { label: 'Other', value: 'Other' },
  ],
  surgicalHistoryYesNoOptions: [
    { label: 'Patient has no surgical history', value: 'Patient has no surgical history' },
    { label: 'Patient has surgical history', value: 'Patient has surgical history' },
  ],
  yesNoOptions: [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ],
  schoolWorkNoteOptions: [
    { label: 'Neither', value: 'Neither' },
    { label: 'School only', value: 'School only' },
    { label: 'Work only', value: 'Work only' },
    { label: 'Both school and work notes', value: 'Both school and work notes' },
  ],
  attorneyOptions: [
    { label: 'I have an attorney', value: 'I have an attorney' },
    { label: 'I do not have an attorney', value: 'I do not have an attorney' },
  ],
  inviteFromAnotherDeviceOptions: [
    { label: 'No, only one device will be connected', value: 'No, only one device will be connected' },
    { label: 'Yes, I will add invite details below', value: 'Yes, I will add invite details below' },
  ],
  inviteContactOptions: [
    { label: 'Email', value: 'Email' },
    { label: 'Phone', value: 'Phone' },
  ],
  // will be automatically added to the encounter if external labs are ordered
  // (does not apply to psc orders)
  externalLabCptCodesToAddPerEncounter: [
    {
      label: 'Handling and/or conveyance of specimen for transfer to a laboratory',
      value: '99001',
    },
  ],
};

const formValueSets: ValueSetsConfig = {
  ...formValueSetsData,
  insuranceTypeOptions: insuranceTypeOptionsData,
};

export const VALUE_SETS = deepFreezeObject(formValueSets);

// Convenience constants for comparing against specific value set options.
// These live here (alongside the arrays they derive from) rather than in config-helpers,
// because ottehr-config modules use them in trigger definitions — importing them from
// config-helpers would create a circular dependency (ottehr-config → config-helpers → ottehr-config).
export const INSURANCE_PAY_OPTION = VALUE_SETS.patientPaymentPageOptions[0].value; // 'I have insurance'
export const SELF_PAY_OPTION = VALUE_SETS.patientPaymentPageOptions[1].value; // 'I will pay without insurance'
export const OCC_MED_SELF_PAY_OPTION = VALUE_SETS.patientOccMedPaymentPageOptions[0].value; // 'Self'
export const OCC_MED_EMPLOYER_PAY_OPTION = VALUE_SETS.patientOccMedPaymentPageOptions[1].value; // 'Employer'
export const ALLERGIES_YES_OPTION = VALUE_SETS.allergiesYesNoOptions[1].value;
export const SURGICAL_HISTORY_YES_OPTION = VALUE_SETS.surgicalHistoryYesNoOptions[1].value;
export const HAS_ATTORNEY_OPTION = VALUE_SETS.attorneyOptions[0].value; // 'I have an attorney'
export const DOES_NOT_HAVE_ATTORNEY_OPTION = VALUE_SETS.attorneyOptions[1].value; // 'I do not have an attorney'
